/**
 * Calendar utility functions for FaceliftDatePicker component
 */

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Get the number of days in a specific month (in NY timezone)
 */
export const getDaysInMonth = (date: Date): number => {
	const nyDate = dayjs(date).tz('America/New_York');
	return nyDate.daysInMonth();
};

/**
 * Get the first day of the week for a specific month (0 = Sunday, 6 = Saturday) (in NY timezone)
 */
export const getFirstDayOfMonth = (date: Date): number => {
	const nyDate = dayjs(date).tz('America/New_York').startOf('month');
	return nyDate.day();
};

/**
 * Generate calendar days array including empty cells for proper layout
 * Returns array of numbers (days) and null values (empty cells)
 */
export const generateCalendarDays = (currentMonth: Date): (number | null)[] => {
	const daysInMonth = getDaysInMonth(currentMonth);
	const firstDay = getFirstDayOfMonth(currentMonth);
	const days: (number | null)[] = [];

	// Add empty cells for days before the first day of the month
	for (let i = 0; i < firstDay; i++) {
		days.push(null);
	}

	// Add actual days of the month
	for (let day = 1; day <= daysInMonth; day++) {
		days.push(day);
	}

	return days;
};

/**
 * Navigate to the previous month (in NY timezone)
 */
export const getPreviousMonth = (currentDate: Date): Date => {
	const nyDate = dayjs(currentDate).tz('America/New_York');
	return nyDate.subtract(1, 'month').startOf('month').toDate();
};

/**
 * Navigate to the next month (in NY timezone)
 */
export const getNextMonth = (currentDate: Date): Date => {
	const nyDate = dayjs(currentDate).tz('America/New_York');
	return nyDate.add(1, 'month').startOf('month').toDate();
};

/**
 * Create a Date object for a specific day in the current month (in NY timezone)
 */
export const createDateForDay = (currentMonth: Date, day: number): Date => {
	const nyDate = dayjs(currentMonth).tz('America/New_York');
	return nyDate.date(day).startOf('day').toDate();
};

/**
 * Check if a specific day is selected (in NY timezone)
 */
export const isDaySelected = (
	currentMonth: Date,
	day: number,
	selectedDate?: Date,
): boolean => {
	if (!selectedDate) return false;

	const dateToCheck = createDateForDay(currentMonth, day);
	const d1 = dayjs(dateToCheck).tz('America/New_York').startOf('day');
	const d2 = dayjs(selectedDate).tz('America/New_York').startOf('day');
	return d1.isSame(d2, 'day');
};

/**
 * Check if a specific day is today (in NY timezone)
 */
export const isDayToday = (currentMonth: Date, day: number): boolean => {
	const today = dayjs().tz('America/New_York').startOf('day');
	const dateToCheck = createDateForDay(currentMonth, day);
	const checkDate = dayjs(dateToCheck).tz('America/New_York').startOf('day');
	return checkDate.isSame(today, 'day');
};

/**
 * Month names for display
 */
export const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
] as const;

/**
 * Day names for calendar header (abbreviated)
 */
export const DAY_NAMES = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

/**
 * Get formatted month and year for calendar header (in NY timezone)
 */
export const getCalendarHeaderText = (currentMonth: Date): string => {
	const nyDate = dayjs(currentMonth).tz('America/New_York');
	return `${MONTH_NAMES[nyDate.month()]} ${nyDate.year()}`;
};
